import { useEffect, useSyncExternalStore } from "react"

/**
 * Light or dark.
 *
 * The choice lives in a cookie rather than on the profile: it belongs to the
 * device as much as the person, and it has to be readable before the user
 * has loaded. With no cookie the app follows the operating system, and keeps
 * following it live if the system switches at sunset.
 */

const COOKIE = "loomina_theme"
const ONE_YEAR = 60 * 60 * 24 * 365
const systemQuery =
	typeof window !== "undefined" && window.matchMedia
		? window.matchMedia("(prefers-color-scheme: dark)")
		: null

const listeners = new Set()

function readCookie() {
	const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=(light|dark)`))
	return match ? match[1] : null
}

function snapshot() {
	const saved = readCookie()
	const system = systemQuery?.matches ? "dark" : "light"
	return `${saved || "system"}:${saved || system}`
}

function notify() {
	listeners.forEach((listener) => listener())
}

function subscribe(listener) {
	listeners.add(listener)
	systemQuery?.addEventListener("change", listener)
	return () => {
		listeners.delete(listener)
		systemQuery?.removeEventListener("change", listener)
	}
}

/** "light" or "dark" pins the theme; "system" clears the cookie. */
export function setThemePreference(preference) {
	if (preference === "light" || preference === "dark") {
		document.cookie = `${COOKIE}=${preference}; path=/; max-age=${ONE_YEAR}; SameSite=Lax`
	} else {
		document.cookie = `${COOKIE}=; path=/; max-age=0; SameSite=Lax`
	}
	notify()
}

/** `preference` is what was chosen ("system", "light", "dark"); `theme` is what shows. */
export function useTheme() {
	const [preference, theme] = useSyncExternalStore(subscribe, snapshot).split(":")
	return { preference, theme, setPreference: setThemePreference }
}

/**
 * Puts the theme on <html> while the calling component is mounted. Only the
 * signed-in app calls it - the marketing and sign-in pages stay light.
 */
export function useApplyTheme() {
	const { theme } = useTheme()
	useEffect(() => {
		const root = document.documentElement
		root.classList.toggle("dark", theme === "dark")
		return () => root.classList.remove("dark")
	}, [theme])
}
